/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { QueryProps } from "../types/query";
import {
  pollExtratoPdfOnce,
  getExtratoPollIntervalMs,
  getExtratoPollMaxTries,
  ExtratoPollResult,
  ExtratoPdfStatus,
  ExtratoForbiddenError,
  ExtratoNotFoundError,
  ExtratoBackendTimeoutError,
} from "../services/extratoPdf";
import { pollUntil, PollTimeoutError } from "../utils/asyncPolling";

// Mensagens dos erros tipados (403/404/503) já são específicas o bastante
// pro usuário. Qualquer outra coisa (erro de rede, CORS, etc.) cai nesse
// texto genérico em vez de expor a mensagem técnica crua (ex: "Network Error").
const GENERIC_EXTRATO_ERROR_MESSAGE = "Não foi possível carregar a consulta. Tente novamente.";

function isKnownExtratoError(
  err: unknown
): err is ExtratoForbiddenError | ExtratoNotFoundError | ExtratoBackendTimeoutError {
  return (
    err instanceof ExtratoForbiddenError ||
    err instanceof ExtratoNotFoundError ||
    err instanceof ExtratoBackendTimeoutError
  );
}

export type ExtratoPhase = "starting" | "processing" | "ready" | "error" | "timeout";
export type ExtratoState = {
  phase: ExtratoPhase;
  progressPercent?: number;
  errorMessage?: string;
  url?: string;
};

export interface UseExtratoPdfResult {
  extrato: ExtratoState | undefined;
  retryExtrato: () => void;
  isShowingExtratoPdf: boolean;
}

// Dispara a geração do extrato em PDF da consulta (se ela tiver um produto
// com extrato habilitado), acompanha o progresso via polling e expõe o
// estado pronto pra tela consumir. Cancela o polling se a consulta mudar ou
// o componente desmontar.
export function useExtratoPdf(query: QueryProps | undefined): UseExtratoPdfResult {
  const [extrato, setExtrato] = useState<ExtratoState | undefined>(undefined);
  const [extratoRetryToken, setExtratoRetryToken] = useState(0);

  const retryExtrato = useCallback(() => {
    setExtratoRetryToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (!query) return;

    const extratoProduct = query.extrato_products?.find(
      (product) => product.extrato_enabled
    );
    if (!extratoProduct) return;

    let cancelled = false;
    let pollCancel: (() => void) | null = null;

    setExtrato({ phase: "starting" });

    function setReady(downloadUrl: string) {
      if (cancelled) return;
      setExtrato({ phase: "ready", url: downloadUrl });
    }

    // "pending" ainda não começou a gerar, "processing" e "failed" (o backend
    // redispara sozinho na próxima chamada) mostram o mesmo indicador de
    // "gerando" — não vale a pena expor o "failed" pro usuário.
    function phaseForStatus(status: ExtratoPdfStatus) {
      return status === "pending" ? "starting" : "processing";
    }

    async function run() {
      // Checa uma vez de cara pra não esperar um intervalo à toa se já
      // vier pronto (ou der erro) na primeira chamada.
      const first = await pollExtratoPdfOnce(query!.id, extratoProduct!.id);
      if (cancelled) return;

      if (first.ready) {
        setReady(first.downloadUrl);
        return;
      }

      setExtrato({ phase: phaseForStatus(first.pdfStatus) });

      const { promise, cancel } = pollUntil<ExtratoPollResult>({
        fetch: () => pollExtratoPdfOnce(query!.id, extratoProduct!.id),
        isDone: (payload) => payload.ready,
        isError: () => false,
        intervalMs: getExtratoPollIntervalMs(),
        maxAttempts: getExtratoPollMaxTries(),
        onTick: (payload) => {
          if (cancelled || payload.ready) return;
          setExtrato({ phase: phaseForStatus(payload.pdfStatus) });
        },
      });
      pollCancel = cancel;

      const finished = await promise;
      if (cancelled || !finished.ready) return;
      setReady(finished.downloadUrl);
    }

    run().catch((err) => {
      if (cancelled) return;
      if (err instanceof PollTimeoutError) {
        setExtrato({ phase: "timeout" });
        return;
      }
      setExtrato({
        phase: "error",
        errorMessage: isKnownExtratoError(err) ? err.message : GENERIC_EXTRATO_ERROR_MESSAGE,
      });
    });

    return () => {
      cancelled = true;
      pollCancel?.();
    };
  }, [query, extratoRetryToken]);

  // Enquanto exibindo (ou tentando exibir) o extrato, o botão "Imprimir" e o
  // fallback em JSON ficam escondidos — só reaparecem se a geração falhar ou
  // estourar o tempo limite (Details.render() cai pro fallback nesse caso).
  const isShowingExtratoPdf =
    (query?.extrato_products ?? []).some((product) => product.extrato_enabled) &&
    extrato?.phase !== "error" &&
    extrato?.phase !== "timeout";

  return { extrato, retryExtrato, isShowingExtratoPdf };
}
